import { Card, type CardProps } from '@mui/material';
import { styled } from '@mui/material/styles';

type DashboardPanelVariant = 'surface' | 'soft' | 'hero';

interface DashboardPanelProps extends CardProps {
  panelVariant?: DashboardPanelVariant;
}

const StyledPanel = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'panelVariant',
})<{ panelVariant: DashboardPanelVariant }>(({ panelVariant, theme }) => {
  const palette = theme.vars?.palette ?? theme.palette;
  const surface = palette.surface ?? {
    blueSoft: palette.action.hover,
    hero: palette.primary.dark,
    heroMuted: palette.text.secondary,
    heroText: palette.primary.contrastText,
    panelBorder: palette.divider,
    primarySoft: palette.action.hover,
    shadow: 'none',
    soft: palette.background.default,
    softStrong: palette.divider,
    successSoft: palette.action.hover,
  };

  return {
    borderRadius: theme.shape.card,
    border: `1px solid ${surface.panelBorder}`,
    background: palette.background.paper,
    boxShadow: surface.shadow,
    ...(panelVariant === 'soft' && {
      borderColor: surface.softStrong,
      background: surface.soft,
      boxShadow: 'none',
    }),
    ...(panelVariant === 'hero' && {
      overflow: 'hidden',
      borderColor: surface.panelBorder,
      background: surface.hero,
      color: surface.heroText,
    }),
  };
});

export function DashboardPanel({
  children,
  panelVariant = 'surface',
  ...cardProps
}: DashboardPanelProps) {
  return (
    <StyledPanel panelVariant={panelVariant} {...cardProps}>
      {children}
    </StyledPanel>
  );
}
